import { computed, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { signalStore, withComputed, withHooks, withMethods, withState, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, exhaustMap, pipe, tap } from 'rxjs';

import { AuthStore } from '@core/auth';
import { StorageService } from '@core/storage';
import { ClockService } from '@shared/utils';

import { CardPreauthApiService } from './card-preauth-api.service';
import { CardPreauthPhase, ICardPreauthEligibility, calendarDaysLeft, cardPreauthPhase, ICampaignPlanBenefit, IPendingPromo } from './card-preauth.models';

/** Where every "confirm" CTA leads: the active subscription's card offers the verification;
 *  with the subscription expired the same page lists the plans to buy again. */
export const CARD_PREAUTH_URL = '/configuration-center/account/subscriptions';

/** The `tomorrow` toast is shown once per browser session. */
const KEY_TOAST_SHOWN = 'aviion.cardPreauth.toastShown';
/** The free-days promo toast is shown once per browser session (its own key: the two never share a session slot). */
const KEY_PROMO_TOAST_SHOWN = 'aviion.promo.toastShown';
/**
 * Session marker written just before handing off to the CorvusPay form for the
 * verification authorization — the payment-ok page reads it to say "card confirmed"
 * instead of "subscription purchased" (both land on the same operator return URL).
 */
const KEY_PENDING = 'aviion.cardPreauth.pending';
/**
 * Session marker written just before handing off to the CorvusPay form for a
 * campaign offer activation — the payment-ok page reads it to show the promo
 * success/failure copy instead of the card-confirmation one. The value is JSON: free days
 * `{ freeTo, firstChargeAt }` (ISO — the BE charges slightly before the free period ends),
 * bonus days `{ type: 'BONUSDAYS', days, chargeAt }` (`chargeAt` ISO at renewal, null when charged now).
 * Deliberately separate from `KEY_PENDING`: the two flows never overlap, but
 * keeping them apart avoids one return page guessing which flow sent it here.
 */
const KEY_PROMO_PENDING = 'aviion.promo.pending';

interface CardPreauthState {
    eligibility: ICardPreauthEligibility | null;
    loaded: boolean;
    /** The toast is open (closed by "Kasnije" or a CTA). */
    toastOpen: boolean;
    /** The expired toast was already opened in this app instance (= this entry). */
    expiredToastShown: boolean;
    /** The free-days promo toast is open (same component, promo variant). */
    promoToastOpen: boolean;
}

const initialState: CardPreauthState = {
    eligibility: null,
    loaded: false,
    toastOpen: false,
    expiredToastShown: false,
    promoToastOpen: false,
};

const valid = (d: Date) => !isNaN(d.getTime());

/** The marker written by `markPendingPromo`; a marker without `type` is a free-days one. */
function parsePendingPromo(m: any): IPendingPromo | null {
    if (m?.type === 'BONUSDAYS') {
        const chargeAt = m.chargeAt ? new Date(m.chargeAt) : null;
        if (!(Number(m.days) > 0) || (chargeAt && !valid(chargeAt))) return null;
        return { type: 'BONUSDAYS', days: Number(m.days), chargeAt };
    }
    const freeTo = new Date(m?.freeTo);
    const firstChargeAt = new Date(m?.firstChargeAt);
    return valid(freeTo) && valid(firstChargeAt) ? { type: 'FREEDAYS', freeTo, firstChargeAt } : null;
}

export const CardPreauthStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),

    withComputed((store, clock = inject(ClockService)) => {
        const expiresAt = computed(() => store.eligibility()?.expiresAt ?? null);
        const promoBenefit = computed(() => store.eligibility()?.campaignBenefit ?? null);
        return {
            expiresAt,
            showBanner: computed(() => store.eligibility()?.showBanner === true),
            eligible: computed(() => store.eligibility()?.eligible === true),
            cardPreregistered: computed(() => store.eligibility()?.cardPreregistered === true),
            verificationAmount: computed(() => store.eligibility()?.verificationAmount ?? 0),
            /** Campaign offer (free days or bonus days) for this customer; null when none applies. */
            promoBenefit,
            /**
             * The header pill and the toast advertise the free days whenever an offer exists. It wins
             * over the card-confirmation notice: activating the offer registers the card as well, so
             * the richer message is the right one. The offer card on the subscriptions page is
             * independent of this flag.
             */
            promoNotice: computed(() => promoBenefit() !== null),
            /** A paid period is still running — a bonus offer is then charged on its expiry day, otherwise now. */
            subscriptionLive: computed(() => (expiresAt()?.getTime() ?? 0) > clock.now()),
            /** The single derived notice state every surface renders from. */
            phase: computed<CardPreauthPhase>(() =>
                cardPreauthPhase(store.eligibility()?.showBanner === true, expiresAt(), clock.now())),
            /** Zagreb calendar days left — tells "sutra" (1) from "danas" (0) within `tomorrow`. */
            daysLeft: computed(() => {
                const at = expiresAt();
                return at ? calendarDaysLeft(at, clock.now()) : null;
            }),
        };
    }),

    withMethods((store, api = inject(CardPreauthApiService), storage = inject(StorageService), router = inject(Router)) => {
        const load = rxMethod<void>(pipe(
            // exhaustMap: the customer signal emits twice on boot (cached hydrate,
            // then server revalidate) for the same customer — one request is enough.
            exhaustMap(() => api.getEligibility().pipe(
                tap(eligibility => patchState(store, { eligibility, loaded: true })),
                // No notice is better than a broken one — the flow is optional.
                catchError(() => { patchState(store, { eligibility: null, loaded: true }); return EMPTY; }),
            )),
        ));
        return {
            load,

            /** Re-asks the BE — after a card confirmation or a cancelled subscription. */
            refresh(): void {
                load();
            },

            clear(): void {
                patchState(store, initialState);
            },

            /** The one CTA behind every notice (header pill, toast, locked play). */
            confirmCard(): void {
                patchState(store, { toastOpen: false });
                router.navigateByUrl(CARD_PREAUTH_URL);
            },

            /** Opens the toast when the phase calls for it: `tomorrow` once per session,
             *  `expired` once per app entry. Storage is SSR-safe (StorageService). */
            openToastIfDue(): void {
                // A free-days offer replaces the card-confirmation toast (see `promoNotice`): it is
                // not opened behind the promo one, so it cannot pop up once the offer is gone.
                if (store.promoNotice()) return;
                const phase = store.phase();
                if (phase === 'tomorrow' && storage.get(KEY_TOAST_SHOWN, 'session') === null) {
                    storage.set(KEY_TOAST_SHOWN, '1', 'session');
                    patchState(store, { toastOpen: true });
                }
                else if (phase === 'expired' && !store.expiredToastShown()) {
                    patchState(store, { toastOpen: true, expiredToastShown: true });
                }
            },

            closeToast(): void {
                patchState(store, { toastOpen: false });
            },

            /** Opens the promo toast once per browser session while the offer is advertised (`promoNotice`). */
            openPromoToastIfDue(): void {
                if (store.promoNotice() && storage.get(KEY_PROMO_TOAST_SHOWN, 'session') === null) {
                    storage.set(KEY_PROMO_TOAST_SHOWN, '1', 'session');
                    patchState(store, { promoToastOpen: true });
                }
            },

            closePromoToast(): void {
                patchState(store, { promoToastOpen: false });
            },

            /** The CTA behind the promo pill and toast: the subscriptions page shows the offer card
             *  (existing subscriber) or the badged plan picker (no subscription). */
            openPromoOffer(): void {
                patchState(store, { promoToastOpen: false });
                router.navigateByUrl(CARD_PREAUTH_URL);
            },

            markPending(): void {
                storage.set(KEY_PENDING, String(Date.now()), 'session');
            },

            /** True (once) when the current payment return belongs to a card confirmation. */
            consumePending(): boolean {
                const pending = storage.get(KEY_PENDING, 'session') !== null;
                storage.remove(KEY_PENDING, 'session');
                return pending;
            },

            /** True when `planReferenceId` is one of the plans a campaign offer covers. */
            promoAppliesTo(planReferenceId: string): boolean {
                return store.promoBenefit()?.plans.some(p => p.planReferenceId === planReferenceId) === true;
            },

            /** The offer for one plan, or null when no campaign covers it. */
            promoBenefitFor(planReferenceId: string): ICampaignPlanBenefit | null {
                return store.promoBenefit()?.plans.find(p => p.planReferenceId === planReferenceId) ?? null;
            },

            /** Session marker written just before handing off to CorvusPay for a promo activation.
             *  `chargeAt` is when a bonus offer charges at renewal (the subscription's expiry). */
            markPendingPromo(offer: ICampaignPlanBenefit, chargeAt: Date | null = null): void {
                const marker = offer.type === 'FREEDAYS'
                    ? { freeTo: offer.freeTo.toISOString(), firstChargeAt: offer.firstChargeAt.toISOString() }
                    : { type: offer.type, days: offer.days, chargeAt: chargeAt?.toISOString() ?? null };
                storage.set(KEY_PROMO_PENDING, JSON.stringify(marker), 'session');
            },

            /** The promo marker (once) when the current payment return belongs to a promo activation;
             *  null when the return is not a promo activation (or the marker is unreadable — the
             *  generic purchase copy then stands). */
            consumePendingPromo(): IPendingPromo | null {
                const marker = storage.get(KEY_PROMO_PENDING, 'session');
                storage.remove(KEY_PROMO_PENDING, 'session');
                if (marker === null) return null;
                try {
                    return parsePendingPromo(JSON.parse(marker));
                } catch {
                    return null;
                }
            },
        };
    }),

    withHooks({
        onInit(store, auth = inject(AuthStore)) {
            // Signed-in customers only; cleared on sign-out.
            effect(() => {
                const customer = auth.customer();
                untracked(() => {
                    if (customer && !customer.anonymous) {
                        if (!store.loaded()) store.load();
                    } else if (store.loaded()) {
                        store.clear();
                    }
                });
            });
            // The toast opens once the answer is in, and again if the phase escalates later
            // in the same session (within its own once-per-session / once-per-entry limits).
            effect(() => {
                store.phase();
                untracked(() => store.openToastIfDue());
            });
            // The promo toast follows the offer the same way, once per session.
            effect(() => {
                store.promoNotice();
                untracked(() => store.openPromoToastIfDue());
            });
        },
    }),
);
